import { memo, useCallback } from 'react'
import { Button, Tooltip } from '@highlight/ui'
import {
  type LocalPathAttachment as LocalPath,
  useLocalPathStatus,
  useReattachLocalPath,
  useRevokeLocalPath,
} from '../../../../store'
import { AttachmentCard } from '../attachment-card'
import { AttachmentStub } from '../attachment-stub'
import { FileTypeIcon } from '../file-type-icon'
import { useRemoveAttachment } from '../hooks/use-remove-attachment'
import type { AttachmentPillProps } from '../types'

/**
 * A file or folder granted from the user's computer. In the composer, removing it revokes the grant. On a sent
 * message it's a pill while this computer holds the grant. After a restart it's a card with Reattach, and on another
 * computer a card saying where the file lives.
 */
export const LocalPathAttachment = memo((attachment: AttachmentPillProps) => {
  const localPath = attachment.attachment as LocalPath
  const removeAttachment = useRemoveAttachment(attachment)
  const revokeLocalPath = useRevokeLocalPath()
  const status = useLocalPathStatus(attachment.conversationId, localPath)
  const reattachLocalPath = useReattachLocalPath()

  const { metadata, conversationId } = attachment
  const onRemove = useCallback(() => {
    removeAttachment?.()
    revokeLocalPath(metadata)
  }, [removeAttachment, revokeLocalPath, metadata])

  const isDirectory = localPath.kind === 'directory'

  if (status === 'on_other_device') {
    return (
      <AttachmentCard
        name={localPath.name}
        isDirectory={isDirectory}
        status={`On ${localPath.deviceName ?? 'another computer'}. Only that computer can open it.`}
      />
    )
  }

  if (status === 'needs_reattach') {
    return (
      <AttachmentCard
        name={localPath.name}
        isDirectory={isDirectory}
        status="No longer attached on this computer"
        action={
          conversationId && (
            <Button size="sm" variant="outline" onClick={() => void reattachLocalPath(conversationId, localPath)}>
              Reattach
            </Button>
          )
        }
      />
    )
  }

  return (
    <Tooltip>
      <Tooltip.Trigger delay={300} render={<div className="flex" />}>
        <AttachmentStub
          label={localPath.name}
          icon={<FileTypeIcon name={localPath.name} isDirectory={isDirectory} />}
          onRemove={removeAttachment && onRemove}
        />
      </Tooltip.Trigger>
      <Tooltip.Content>
        {localPath.path} · {localPath.mode === 'read' ? 'Read only' : 'Can edit'}
      </Tooltip.Content>
    </Tooltip>
  )
})
LocalPathAttachment.displayName = 'LocalPathAttachment'
